<template>
  <v-container class="pa-0">
    <v-row v-if="loading" dense class="mt-9" align="center" justify="center">
      <v-progress-circular indeterminate :color="theme.primary" />
    </v-row>

    <template v-else>
      <v-row dense class="mt-9">
        <v-col cols="12">
          <v-card
            class="notification-header"
            elevation="1"
            :style="headerStyle"
          >
            <v-card-text class="pa-6">
              <div class="d-flex justify-space-between align-start flex-wrap">
                <div class="mr-4">
                  <div class="notification-header__eyebrow">
                    Central de mensagens
                  </div>
                  <div class="notification-header__title">
                    {{ title }}
                  </div>
                  <div class="notification-header__subtitle">
                    {{ subtitle }}
                  </div>
                </div>

                <v-chip small dark class="mt-2 mt-sm-0" :color="theme.primary">
                  {{ unreadCount }} não lida<span v-if="unreadCount !== 1"
                    >s</span
                  >
                </v-chip>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-8">
        <v-col cols="12">
          <v-card class="notification-shell" elevation="2" :style="shellStyle">
            <v-row no-gutters>
              <v-col cols="12" md="4" class="notification-shell__sidebar">
                <div class="notification-shell__sidebar-header">
                  <div class="notification-shell__sidebar-title">
                    Caixa de entrada
                  </div>
                  <div class="notification-shell__sidebar-subtitle">
                    Clique para visualizar a mensagem
                  </div>
                </div>

                <div v-if="notifications.length" class="notification-list">
                  <button
                    v-for="item in notifications"
                    :key="item.id"
                    type="button"
                    class="notification-row"
                    :class="{
                      'notification-row--selected':
                        selectedNotification &&
                        selectedNotification.id === item.id,
                      'notification-row--unread': !item.read,
                    }"
                    @click="$emit('select', item)"
                  >
                    <div class="notification-row__top">
                      <div class="d-flex align-center">
                        <span
                          class="notification-row__dot"
                          :class="{
                            'notification-row__dot--hidden': item.read,
                          }"
                        />
                        <span class="notification-row__title">
                          {{ item.title }}
                        </span>
                      </div>

                      <span class="notification-row__date">
                        {{ item.date }}
                      </span>
                    </div>

                    <div class="notification-row__bottom">
                      <span class="notification-row__snippet">
                        {{ snippet(item.description) }}
                      </span>

                      <v-chip
                        v-if="item.voting"
                        x-small
                        outlined
                        class="ml-2"
                        :color="theme.secondary"
                      >
                        Votação
                      </v-chip>
                    </div>
                  </button>
                </div>

                <div v-else class="notification-empty-state">
                  <v-icon large :color="theme.secondary">
                    mdi-bell-outline
                  </v-icon>
                  <div class="notification-empty-state__title">
                    Nenhuma notificação por aqui
                  </div>
                  <div class="notification-empty-state__text">
                    Assim que surgir um novo comunicado, ele aparecerá nesta
                    lista.
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="8" class="notification-shell__content">
                <div v-if="selectedNotification" class="notification-preview">
                  <div class="notification-preview__header">
                    <div>
                      <div class="notification-preview__title">
                        {{ selectedNotification.title }}
                      </div>
                      <div class="notification-preview__meta">
                        {{ selectedNotification.date }}
                      </div>
                    </div>

                    <v-chip
                      small
                      outlined
                      dark
                      class="notification-preview__status"
                      :color="
                        selectedNotification.read
                          ? theme.secondary
                          : theme.primary
                      "
                    >
                      {{ selectedNotification.read ? "Lida" : "Não lida" }}
                    </v-chip>
                  </div>

                  <v-divider class="my-5" />

                  <div class="notification-preview__body">
                    {{ selectedNotification.description }}
                  </div>

                  <div
                    v-if="selectedNotification.voting"
                    class="notification-voting mt-8"
                  >
                    <div class="notification-voting__title">
                      Votação disponível
                    </div>
                    <div class="notification-voting__text">
                      Esta notificação exige um posicionamento seu sobre a
                      alteração proposta.
                    </div>

                    <div class="d-flex flex-wrap mt-4">
                      <v-btn
                        dark
                        rounded
                        class="mr-3 mb-3"
                        :color="theme.primary"
                        :disabled="voted || votingBusy"
                        :loading="votingBusy"
                        @click="
                          $emit('vote', {
                            voteId: selectedNotification.voting,
                            option: true,
                          })
                        "
                      >
                        <v-icon small left>mdi-thumb-up-outline</v-icon>
                        Concordo
                      </v-btn>

                      <v-btn
                        rounded
                        outlined
                        class="mb-3"
                        :color="theme.secondary"
                        :disabled="voted || votingBusy"
                        @click="
                          $emit('vote', {
                            voteId: selectedNotification.voting,
                            option: false,
                          })
                        "
                      >
                        <v-icon small left>mdi-thumb-down-outline</v-icon>
                        Discordo
                      </v-btn>
                    </div>

                    <div v-if="voted" class="notification-voting__feedback">
                      Seu voto já foi registrado nesta notificação.
                    </div>
                  </div>

                  <div
                    v-if="generalClassInvite"
                    class="notification-voting mt-8"
                  >
                    <div class="notification-voting__title">
                      Confirme sua participação
                    </div>
                    <div class="notification-voting__text">
                      Informe se você irá participar desta aula de conversação.
                    </div>

                    <div class="d-flex flex-wrap mt-4">
                      <v-btn
                        dark
                        rounded
                        class="mr-3 mb-3"
                        :color="theme.primary"
                        :disabled="generalClassAnswered || generalClassBusy"
                        :loading="generalClassBusy"
                        @click="
                          $emit('confirm-general-class', {
                            sessionId: generalClassInvite,
                          })
                        "
                      >
                        <v-icon small left>mdi-check</v-icon>
                        Participarei
                      </v-btn>

                      <v-btn
                        rounded
                        outlined
                        class="mb-3"
                        :color="theme.secondary"
                        :disabled="generalClassAnswered || generalClassBusy"
                        @click="
                          $emit('decline-general-class', {
                            sessionId: generalClassInvite,
                          })
                        "
                      >
                        <v-icon small left>mdi-close</v-icon>
                        Não participarei
                      </v-btn>
                    </div>

                    <div
                      v-if="generalClassAnswered"
                      class="notification-voting__feedback"
                    >
                      Sua resposta já foi registrada.
                    </div>
                  </div>
                </div>

                <div v-else class="notification-empty-preview">
                  <v-icon large :color="theme.secondary">
                    mdi-email-open-outline
                  </v-icon>
                  <div class="notification-empty-preview__title">
                    Selecione uma notificação
                  </div>
                  <div class="notification-empty-preview__text">
                    Escolha um item da lista lateral para ver o conteúdo
                    completo aqui.
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <v-pagination
        v-if="showPagination"
        class="d-flex align-center justify-center mt-6"
        circle
        :length="totalPages"
        :value="currentPage"
        @input="$emit('page-change', $event)"
      />
    </template>
  </v-container>
</template>

<script>
export default {
  name: "NotificationInbox",

  props: {
    title: {
      type: String,
      default: "Minhas notificações",
    },
    subtitle: {
      type: String,
      default: "Visualize todas as notificações e comunicados.",
    },
    notifications: {
      type: Array,
      default: () => [],
    },
    selectedNotification: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
    unreadCount: {
      type: Number,
      default: 0,
    },
    voted: {
      type: Boolean,
      default: false,
    },
    votingBusy: {
      type: Boolean,
      default: false,
    },
    generalClassAnswered: {
      type: Boolean,
      default: false,
    },
    generalClassBusy: {
      type: Boolean,
      default: false,
    },
    theme: {
      type: Object,
      required: true,
    },
  },

  computed: {
    generalClassInvite() {
      const reference = this.selectedNotification?.reference;
      if (!reference) return null;
      const match = String(reference).match(/^general-class:([^:]+):invite/);
      return match ? match[1] : null;
    },
    headerStyle() {
      return {
        "--notify-primary": this.theme.primary,
        "--notify-secondary": this.theme.secondary,
      };
    },
    shellStyle() {
      return {
        "--notify-primary": this.theme.primary,
        "--notify-secondary": this.theme.secondary,
        "--notify-surface": this.theme.surface,
        "--notify-surface-alt": this.theme.surfaceAlt || this.theme.surface,
        "--notify-border": this.theme.border,
      };
    },
    showPagination() {
      return this.totalPages > 1;
    },
  },

  methods: {
    snippet(description) {
      if (!description) return "Sem descrição disponível.";

      const normalized = description.replace(/\s+/g, " ").trim();

      return normalized.length > 82
        ? `${normalized.slice(0, 79)}...`
        : normalized;
    },
  },
};
</script>

<style scoped lang="css">
.notification-header {
  border-radius: 24px;
  background: linear-gradient(
    135deg,
    var(--notify-primary) 0%,
    var(--notify-secondary) 100%
  );
  color: #fff;
  overflow: hidden;
}

.notification-header__eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #fff;
  opacity: 1;
  text-transform: uppercase;
}

.notification-header__title {
  margin-top: 10px;
  color: #fff;
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1.1;
}

.notification-header__subtitle {
  margin-top: 10px;
  max-width: 540px;
  color: #fff;
  font-size: 0.96rem;
  line-height: 1.6;
  opacity: 0.92;
}

.notification-shell {
  overflow: hidden;
  border-radius: 26px;
  border: 1px solid var(--notify-border);
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.96) 0%,
    var(--notify-surface) 100%
  );
  box-shadow: 0 32px 70px -52px rgba(15, 23, 42, 0.48);
}

.notification-shell__sidebar {
  min-height: 640px;
  border-right: 1px solid rgba(148, 163, 184, 0.16);
  background: linear-gradient(
    180deg,
    var(--notify-surface-alt) 0%,
    rgba(255, 255, 255, 0.98) 100%
  );
}

.notification-shell__sidebar-header {
  padding: 28px 28px 22px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.16);
}

.notification-shell__sidebar-title {
  color: #111827;
  font-size: 1rem;
  font-weight: 700;
}

.notification-shell__sidebar-subtitle {
  margin-top: 4px;
  color: #6b7280;
  font-size: 0.85rem;
}

.notification-list {
  padding: 14px;
}

.notification-row {
  width: 100%;
  margin-bottom: 10px;
  padding: 18px 20px;
  border: 1px solid transparent;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.94);
  cursor: pointer;
  text-align: left;
  transition: transform 0.18s ease, border-color 0.18s ease,
    box-shadow 0.18s ease, background-color 0.18s ease;
}

.notification-row:hover {
  transform: translateY(-1px);
  border-color: var(--notify-border);
  box-shadow: 0 20px 30px -28px rgba(15, 23, 42, 0.38);
}

.notification-row--selected {
  border-color: var(--notify-primary);
  background: var(--notify-surface-alt);
}

.notification-row--unread .notification-row__title {
  color: #111827;
  font-weight: 700;
}

.notification-row__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.notification-row__dot {
  width: 8px;
  height: 8px;
  margin-right: 10px;
  border-radius: 999px;
  background: var(--notify-primary);
  flex-shrink: 0;
}

.notification-row__dot--hidden {
  opacity: 0;
}

.notification-row__title {
  color: #374151;
  font-size: 0.95rem;
  font-weight: 600;
}

.notification-row__date {
  color: #6b7280;
  font-size: 0.75rem;
  white-space: nowrap;
}

.notification-row__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
}

.notification-row__snippet {
  color: #6b7280;
  font-size: 0.83rem;
  line-height: 1.5;
}

.notification-shell__content {
  min-height: 640px;
  padding: 34px;
}

.notification-preview {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.notification-preview__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 26px 28px;
  border-radius: 24px;
  background: linear-gradient(
    135deg,
    var(--notify-primary) 0%,
    var(--notify-secondary) 100%
  );
  color: #fff;
}

.notification-preview__title {
  color: #fff;
  font-size: 1.45rem;
  font-weight: 700;
  line-height: 1.2;
}

.notification-preview__meta {
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.86rem;
}

.notification-preview__body {
  padding: 28px 30px;
  border-radius: 22px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(255, 255, 255, 0.92);
  color: #374151;
  font-size: 0.97rem;
  line-height: 1.8;
  white-space: pre-line;
}

.notification-preview__status {
  border-color: rgba(255, 255, 255, 0.48) !important;
  color: #fff !important;
}

.notification-preview__status .v-chip__content {
  color: #fff !important;
}

.notification-voting {
  padding: 26px;
  border: 1px solid var(--notify-border);
  border-radius: 20px;
  background: linear-gradient(180deg, var(--notify-surface-alt) 0%, white 100%);
}

.notification-voting__title {
  color: #111827;
  font-size: 1rem;
  font-weight: 700;
}

.notification-voting__text {
  margin-top: 8px;
  color: #4b5563;
  line-height: 1.6;
}

.notification-voting__feedback {
  color: var(--notify-secondary);
  font-size: 0.84rem;
  font-weight: 600;
}

.notification-empty-state,
.notification-empty-preview {
  min-height: 100%;
  padding: 48px 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
}

.notification-empty-state__title,
.notification-empty-preview__title {
  margin-top: 18px;
  color: #111827;
  font-size: 1rem;
  font-weight: 700;
}

.notification-empty-state__text,
.notification-empty-preview__text {
  margin-top: 8px;
  max-width: 320px;
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.6;
}

@media (max-width: 959px) {
  .notification-shell__sidebar {
    min-height: auto;
    border-right: 0;
    border-bottom: 1px solid rgba(148, 163, 184, 0.16);
  }

  .notification-shell__content {
    min-height: 420px;
    padding: 24px;
  }
}
</style>
